import { useState } from 'react';
import { AiSurface } from '@/components/ui/ai-surface';
import { AiConfidenceChip } from '@/components/ui/ai-confidence-chip';
import { CitationChip, type CitationChipData } from '@/components/ui/citation-chip';
import { CitationSourceModal } from '@/components/modals/citation-source-modal';
import {
  deriveCitationConfidence,
  deriveMessageConfidence,
  type ChatCitation,
} from '@/lib/api/chat';
import { cn } from '@/lib/utils/cn';

/**
 * T-0100 — AI chat bubble. Left-aligned, `<AiSurface as="div">`
 * treatment with the lavender background, ink-purple text, and lavender
 * border-left rail. Carries the `<AiConfidenceChip>` ABOVE the answer
 * (per ticket §AI messages — "above the answer"), the answer text
 * itself, the citation chip row, and an optional cost meter.
 *
 * No-evidence path: when `no_evidence=true` we render the empty-state
 * refusal copy verbatim per ticket scope ("I couldn't answer this from
 * the engagement's documents. Try uploading more sources or rephrasing.")
 * The confidence chip still shows (low band) — the user must see at a
 * glance that this is a refusal, not a fabricated answer.
 *
 * Streaming: the consumer passes `displayContent` (which equals
 * `content` when the message is fully revealed). The bubble doesn't
 * own the timer — that's the parent's job — so refresh cycles stay
 * cheap and the cancel button can short-circuit cleanly.
 *
 * Required reading:
 *   - `design_handoff_caliber_v2/prototype/screens-engagement.js` →
 *     `chatMsgAssistant(intro, bullets, sources, conf)`
 *   - `design_handoff_caliber_v2/README.md` §8 pattern 4 (citation
 *     round-trip)
 *   - CLAUDE.md hard rule #3 (load-bearing primitives — built once,
 *     reused everywhere; we delegate to `<AiSurface>`, `<AiConfidenceChip>`,
 *     `<CitationChip>`, `<CitationSourceModal>`).
 */

export interface AiMessageProps {
  /** Engagement UUID — needed for the citation source modal fetch. */
  engagementId: string;
  /**
   * Authoritative content from the API. Used as the source of truth
   * (e.g. when the streaming finishes the parent passes content ===
   * displayContent).
   */
  content: string;
  /**
   * What's currently visible on screen. During faked streaming the
   * parent reveals char-by-char or word-by-word; once the reveal is
   * complete this equals `content`.
   */
  displayContent: string;
  /** Optional model confidence; falls back to `deriveMessageConfidence`. */
  confidence?: number | null;
  /** When true, render the no-evidence refusal card instead of the answer. */
  noEvidence?: boolean | null;
  /**
   * Per-message cost in cents — Optional. Render the cost meter
   * footer when present (BudgetGate ledger sum for the turn).
   */
  tokenCostCents?: number | null;
  citations: readonly ChatCitation[];
  /**
   * True while the parent is still revealing characters. The bubble
   * shows a subtle pulsing dot at the tail of the visible text to
   * read as "still typing". When done, the dot disappears.
   */
  streaming?: boolean;
  /**
   * Optional measured latency in milliseconds — the parent computes
   * this from `performance.now()` between submit and response. When
   * present, the bubble header shows "answered in Xs" per the
   * prototype (line 622). Backend does not persist latency today, so
   * older transcript messages omit this stamp.
   */
  latencyMs?: number | null;
  className?: string;
}

const NO_EVIDENCE_COPY =
  "I couldn't answer this from the engagement's documents. Try uploading more sources or rephrasing.";

export function AiMessage({
  engagementId,
  content,
  displayContent,
  confidence,
  noEvidence,
  tokenCostCents,
  citations,
  streaming,
  latencyMs,
  className,
}: AiMessageProps) {
  // The chip's confidence value: prefer the API-supplied number, fall
  // back to the heuristic derivation. Both surfaces are kept in
  // `lib/api/chat.ts` so a future backend widening only touches that
  // file.
  const messageConfidence = deriveMessageConfidence({
    confidence,
    no_evidence: noEvidence,
    citations,
  });

  // Map the API citation rows onto the CitationChip data envelope so
  // the load-bearing primitive owns the visual contract.
  const chipData: CitationChipData[] = citations.map((c) => ({
    id: c.citation_id,
    doc_label: c.document_title,
    section: c.section_path ?? null,
    page: c.page_number ?? null,
    confidence: deriveCitationConfidence(c),
    status: 'ok',
  }));

  // The citation source modal is launched per-chip. We track which
  // chip opened it so the modal hydrates the right citation_id.
  const [openCite, setOpenCite] = useState<CitationChipData | null>(null);

  return (
    <div className={cn('flex gap-3', className)}>
      {/* Gutter mirrors the prototype's 24-px left rail — keeps the AI
          bubble visually offset from the user bubble without an
          avatar. */}
      <div className="w-6 shrink-0" aria-hidden="true" />
      <AiSurface
        as="div"
        // Override the default border on all four sides — we want a
        // single 2-px lavender rail on the leading edge plus the
        // asymmetric radius from the prototype.
        // `min-w-0` + `overflow-wrap:anywhere`: a long file name or URL in
        // the answer wraps inside the bubble instead of widening it past
        // the panel's right edge at tablet width (C2-35).
        className={cn(
          'min-w-0 flex-1 border-l-2 border-y-0 border-r-0 bg-ai-surface text-ai-ink',
          'max-w-[760px] rounded-[0_12px_12px_12px] px-[18px] py-[14px] [overflow-wrap:anywhere]',
        )}
        role="article"
        aria-label="AI answer"
      >
        {/* Header strip: "Caliber" + optional latency stamp + confidence
            chip pinned right. Mirrors the prototype's small uppercase
            "Caliber" tag + the "·" separator + "answered in Xs" time
            stamp (line 622 of screens-engagement.js). Latency is the
            real measured client-side elapsed time — not a synthesized
            number. When the parent didn't measure it (e.g. for older
            transcript rows that the API doesn't carry latency for), we
            omit the stamp. */}
        <div className="mb-2 flex flex-wrap items-center gap-2">
          <span className="text-[11px] font-semibold uppercase tracking-[0.04em] text-ai-ink">
            Caliber
          </span>
          {typeof latencyMs === 'number' ? (
            <>
              <span className="text-meta text-ai-ink" aria-hidden="true">
                ·
              </span>
              <span className="text-meta text-ai-ink">answered in {formatLatency(latencyMs)}</span>
            </>
          ) : null}
          <span className="ml-auto">
            <AiConfidenceChip value={messageConfidence} />
          </span>
        </div>

        {noEvidence ? (
          <NoEvidenceBody />
        ) : (
          <p className="whitespace-pre-wrap text-body leading-[22px] text-ink-700">
            {displayContent}
            {streaming ? <StreamingCursor /> : null}
          </p>
        )}

        {!noEvidence && chipData.length > 0 ? (
          <CitationRow
            cites={chipData}
            onOpen={(id) => {
              const target = chipData.find((c) => c.id === id);
              if (target) setOpenCite(target);
            }}
          />
        ) : null}

        {typeof tokenCostCents === 'number' ? <CostMeter cents={tokenCostCents} /> : null}
      </AiSurface>

      {openCite ? (
        <CitationSourceModal
          engagementId={engagementId}
          cite={openCite}
          open={openCite != null}
          onOpenChange={(open) => {
            if (!open) setOpenCite(null);
          }}
        />
      ) : null}

      {/* aria-live announcer for screen readers — when content changes
          we announce the final answer once it lands. During streaming
          we don't announce each chunk (would spam SR users). */}
      {!streaming && !noEvidence ? (
        <p className="sr-only" aria-live="polite">
          {content}
        </p>
      ) : null}
    </div>
  );
}

/**
 * No-evidence card. Visually distinguishes from a normal answer with
 * a dashed border + paper background so the user can't miss it.
 * Copy is verbatim from the ticket scope.
 */
function NoEvidenceBody() {
  return (
    <div
      role="status"
      className={cn(
        'rounded-card border border-dashed border-ai-surface-border bg-paper-0 p-4',
        'text-body text-ink-700',
      )}
    >
      <p>{NO_EVIDENCE_COPY}</p>
    </div>
  );
}

/**
 * Citation chip row under the answer. Each chip wires to the
 * `<CitationSourceModal>` on click — completes the round-trip in
 * §8 pattern 4.
 */
function CitationRow({
  cites,
  onOpen,
}: {
  cites: readonly CitationChipData[];
  onOpen: (id: string) => void;
}) {
  return (
    <div className="mt-3 border-t border-ai-surface-border pt-[10px]">
      <div className="mb-1.5 text-[11px] font-semibold uppercase tracking-[0.04em] text-ai-ink">
        Sources · {cites.length}
      </div>
      <ul className="flex flex-wrap gap-1.5">
        {cites.map((c) => (
          <li key={c.id} className="min-w-0 max-w-full">
            <CitationChip cite={c} onOpen={onOpen} />
          </li>
        ))}
      </ul>
    </div>
  );
}

/**
 * Per-message cost footer. Renders the cents value in cost format. v1
 * displays the cents the API reports; we don't compute totals here —
 * the AI Costs surface (T-014, settings/ai-costs) owns the firm-wide
 * rollup.
 */
function CostMeter({ cents }: { cents: number }) {
  const usd = (cents / 100).toFixed(cents < 100 ? 4 : 2);
  return (
    <div className="mt-3 flex items-center gap-1.5 text-[11px] font-medium text-ink-500">
      <span className="font-mono">$ {usd}</span>
      <span aria-hidden="true">·</span>
      <span>cost</span>
    </div>
  );
}

/**
 * Format the measured client-side latency for the header stamp.
 * Sub-second reads show 2-decimal seconds ("0.42s"); >=1s shows 1
 * decimal ("1.4s"); >=10s shows whole seconds.
 */
function formatLatency(ms: number): string {
  if (ms < 1000) return `${(ms / 1000).toFixed(2)}s`;
  if (ms < 10_000) return `${(ms / 1000).toFixed(1)}s`;
  return `${Math.round(ms / 1000)}s`;
}

/**
 * Pulsing dot at the tail of the visible text during faked streaming
 * (see parent for the timer). Pure-CSS, respects reduced motion: the
 * `animate-pulse` utility is automatically suppressed by Tailwind's
 * `motion-safe` defaults under `prefers-reduced-motion`.
 */
function StreamingCursor() {
  return (
    <span
      aria-hidden="true"
      className="ml-0.5 inline-block h-[14px] w-[6px] translate-y-0.5 animate-pulse rounded-sm bg-ai-ink"
    />
  );
}
